import { useEffect, useRef, useState } from "react";
import SearchBar from "./SearchBar";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { useLanguage } from "@/context/LanguageContext";
import SortSelector from "./SortSelector";
import { LayoutGrid, List, Rows } from "lucide-react";
import { Button } from "../ui/button";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
interface FilterState {
  make: string;
  model: string;
  minMileage: string;
  maxMileage: string;
  minYear: string;
  maxYear: string;
  transmission: string;
  fuelType: string;
  minPower: string;
  maxPower: string;
  bodyType: string;
  minPrice: string;
  maxPrice: string;
}

interface FilterBarProps {
  searchTerm: string;
  sortBy: string;
  filters: FilterState;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onSortChange: (value: string) => void;
  onFiltersChange: (filters: FilterState) => void;
  onClearFilters: () => void;
  totalCars: number;
  viewMode?: "wide" | "narrow" | "grid";
  onViewModeChange?: (mode: "wide" | "narrow" | "grid") => void;
  showViewToggle?: boolean;
  hasActiveSearch?: boolean;
  onClearSearch?: () => void;
  /** Native all-listings: sort via floating FAB instead. */
  hideSortControls?: boolean;
  /** Native: optional extra controls in the row (legacy). */
  nativeInlineControls?: ReactNode;
  /** Native: called when the filter bar scrolls in/out of view (for floating sort). */
  onNativeBarVisibilityChange?: (visible: boolean) => void;
}

/** Site header: Layout main padding + fixed header (top-4 + h-14/h-12). */
const PINNED_TOP_CLASS =
  "max-sm:top-[calc(1rem+3.5rem+6px)] sm:top-16";

/**
 * /auctions browse shell: site Header is hidden; pin below Alla|För dig tabs
 * (safe-area + ~3.75rem header row). Above header fade (z-40).
 */
const PINNED_TOP_AUCTIONS_BROWSE_CLASS =
  "top-[calc(env(safe-area-inset-top,0px)+3.75rem)]";

const NATIVE_PINNED_TOP_CLASS =
  "top-[calc(var(--native-safe-top)+0.375rem)]";

function isAuctionsBrowseShell(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/auctions" || path === "/auctions/all";
}

function getStickyTopOffset(isNative: boolean): number {
  if (typeof window === "undefined") return isNative ? 0 : 64;
  if (isNative) {
    const root = document.documentElement;
    const safeTop =
      parseFloat(
        getComputedStyle(root).getPropertyValue("--native-safe-top"),
      ) || 0;
    return safeTop + 6;
  }
  if (isAuctionsBrowseShell()) {
    // Match fixed Alla|För dig header spacer (~3.75rem + safe-area)
    return 60 + (window.visualViewport?.offsetTop ?? 0);
  }
  return window.matchMedia("(max-width: 639px)").matches
    ? 16 + 56 + 6
    : 64;
}

export const FilterBar = ({
  searchTerm,
  sortBy,
  filters,
  onSearchChange,
  onSearch,
  onSortChange,
  onFiltersChange,
  onClearFilters,
  totalCars,
  viewMode,
  onViewModeChange,
  showViewToggle = false,
  hasActiveSearch = false,
  onClearSearch,
  hideSortControls = false,
  nativeInlineControls,
  onNativeBarVisibilityChange,
}: FilterBarProps) => {
  const { language } = useLanguage();
  const labels = auctionsLabels(language);
  const { isNative } = useNativePlatform();
  const nativeAnchorRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const [pinBox, setPinBox] = useState<{ left: number; width: number } | null>(
    null,
  );
  const [anchorInView, setAnchorInView] = useState(true);

  useEffect(() => {
    const placeholder = placeholderRef.current;
    const bar = barRef.current;
    if (!placeholder || !bar) return;

    const updatePinned = () => {
      const offset = getStickyTopOffset(isNative);
      const phRect = placeholder.getBoundingClientRect();
      const shouldPin = phRect.top < offset;
      setIsPinned(shouldPin);
      setBarHeight(bar.offsetHeight);
      if (shouldPin) {
        // Match the in-flow bar size: placeholder has px-2 on mobile, so the
        // child bar sits in the content box — not the full border box.
        const cs = getComputedStyle(placeholder);
        const padL = parseFloat(cs.paddingLeft) || 0;
        const padR = parseFloat(cs.paddingRight) || 0;
        setPinBox({
          left: phRect.left + padL,
          width: Math.max(0, phRect.width - padL - padR),
        });
      } else {
        setPinBox(null);
      }
    };

    updatePinned();
    window.addEventListener("scroll", updatePinned, { passive: true });
    window.addEventListener("resize", updatePinned);
    return () => {
      window.removeEventListener("scroll", updatePinned);
      window.removeEventListener("resize", updatePinned);
    };
  }, [isNative]);

  useEffect(() => {
    if (!isNative || !onNativeBarVisibilityChange) return;

    const node = nativeAnchorRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setAnchorInView(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-8px 0px 0px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isNative, onNativeBarVisibilityChange]);

  useEffect(() => {
    if (!isNative || !onNativeBarVisibilityChange) return;
    onNativeBarVisibilityChange(anchorInView && !isPinned);
  }, [anchorInView, isPinned, isNative, onNativeBarVisibilityChange]);

  const searchRow = (
    <div className="flex-1 w-full min-w-0">
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
        hasActiveSearch={hasActiveSearch}
        onClearSearch={onClearSearch}
      />
    </div>
  );

  const controlsRow = (
    <div
      className={cn(
        "flex items-center gap-2 md:gap-4 w-full md:w-auto justify-end md:justify-start",
        isNative && isPinned && "hidden",
      )}
    >
      {!hideSortControls && (
        <SortSelector sortBy={sortBy} onSortChange={onSortChange} />
      )}

      {hideSortControls &&
        isNative &&
        nativeInlineControls &&
        anchorInView &&
        !isPinned &&
        nativeInlineControls}

      <div className="flex items-center gap-2 ml-auto md:ml-0">
        <div className="text-sm md:text-sm text-primary/60 shrink-0">
          {totalCars} {labels.filterBar.vehicles}
        </div>

        {showViewToggle && viewMode && onViewModeChange && (
          <div className="flex items-center bg-card border rounded-lg">
            <Button
              variant={viewMode === "wide" ? "default" : "ghost"}
              size="sm"
              onClick={() => onViewModeChange("wide")}
              className="h-6 p-1"
              aria-label={labels.viewModes?.wide ?? "Wide"}
              title={labels.viewModes?.wide ?? "Wide"}
            >
              <Rows className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "narrow" ? "default" : "ghost"}
              size="sm"
              onClick={() => onViewModeChange("narrow")}
              className="h-6 p-1"
              aria-label={labels.viewModes?.narrow ?? "List"}
              title={labels.viewModes?.narrow ?? "List"}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => onViewModeChange("grid")}
              className="h-6 p-1"
              aria-label={labels.viewModes?.grid ?? "Grid"}
              title={labels.viewModes?.grid ?? "Grid"}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const barInner = (
    <div
      className={cn(
        "flex flex-col md:flex-row gap-2 md:gap-4 p-2 md:p-4 rounded-lg",
        isNative ? "p-1 gap-2 items-stretch" : "items-center",
      )}
    >
      {searchRow}
      {controlsRow}
    </div>
  );

  return (
    <div
      ref={(node) => {
        placeholderRef.current = node;
        nativeAnchorRef.current = node;
      }}
      className={cn(
        "relative mb-2 w-full min-w-0",
        isNative ? "px-1" : "-mx-2 sm:mx-0 px-2 sm:px-0",
      )}
      style={isPinned && barHeight > 0 ? { height: barHeight } : undefined}
    >
      <div
        ref={barRef}
        className={cn(
          "bg-background/95 backdrop-blur-sm border-b border-border/40 w-full",
          // Above fixed Alla|För dig header fade (z-40)
          isPinned ? "z-50" : "z-30",
          isPinned &&
            cn(
              "fixed shadow-sm",
              isNative
                ? NATIVE_PINNED_TOP_CLASS
                : isAuctionsBrowseShell()
                  ? PINNED_TOP_AUCTIONS_BROWSE_CLASS
                  : PINNED_TOP_CLASS,
              isNative && "py-0 !border-b-0",
            ),
        )}
        style={
          isPinned && pinBox
            ? { left: pinBox.left, width: pinBox.width }
            : undefined
        }
      >
        {barInner}
      </div>
    </div>
  );
};
